/**
 * 🏢 ORGANIZATION HOOK - Clerk-Only Implementation
 * 
 * React hook for managing organization context using Clerk SDK:
 * - Get user's organizations from Clerk
 * - Get current/active organization
 * - Switch between organizations using Clerk
 */

import { useOrganization as useClerkOrganization, useOrganizationList, useAuth } from "@clerk/clerk-react";
import { useState, useCallback } from "react";

// Clerk organization mapped to our app's format
export type Organization = {
  id: string; // Clerk organization ID
  name: string;
  slug: string;
  role: string; // Clerk role: org:admin, org:member, etc.
  imageUrl?: string;
  createdAt: number;
};

interface UseOrganizationReturn {
  // Current state
  organizations: Organization[];
  currentOrganization: Organization | null;
  isLoading: boolean;
  
  // Actions
  switchOrganization: (organizationId: string) => Promise<void>;
  
  // Helpers
  userRole: string | null;
  isOwner: boolean;
  isAdmin: boolean;
  isMember: boolean;
  
  // Clerk-specific
  clerkOrganization: ReturnType<typeof useClerkOrganization>['organization'];
}

export function useOrganization(): UseOrganizationReturn {
  const { isLoaded: authLoaded } = useAuth();
  const { organization: clerkOrg, isLoaded: orgLoaded } = useClerkOrganization();
  const { 
    isLoaded: listLoaded, 
    setActive,
    userMemberships 
  } = useOrganizationList({
    userMemberships: {
      infinite: true, // Load all organizations
    },
  });
  
  const [isSwitching, setIsSwitching] = useState(false);
  
  // Map Clerk organizations to our format
  const organizations: Organization[] = userMemberships?.data?.map((membership) => ({
    id: membership.organization.id,
    name: membership.organization.name,
    slug: membership.organization.slug || membership.organization.id,
    role: membership.role,
    imageUrl: membership.organization.imageUrl,
    createdAt: membership.organization.createdAt?.getTime() || Date.now(),
  })) || [];
  
  // Current organization from Clerk
  // Find the current user's membership in this organization
  const currentMembership = userMemberships?.data?.find(
    m => m.organization.id === clerkOrg?.id
  );
  
  const currentOrganization: Organization | null = clerkOrg ? {
    id: clerkOrg.id,
    name: clerkOrg.name,
    slug: clerkOrg.slug || clerkOrg.id,
    role: currentMembership?.role || 'org:member',
    imageUrl: clerkOrg.imageUrl,
    createdAt: clerkOrg.createdAt?.getTime() || Date.now(),
  } : null;
  
  /**
   * Switch to a different organization using Clerk SDK
   */
  const switchOrganization = useCallback(async (orgId: string) => {
    if (!setActive) {
      console.error("Clerk setActive not available");
      return;
    }
    
    setIsSwitching(true);
    try {
      await setActive({ organization: orgId });
      // Clerk will automatically update the JWT and trigger re-renders
    } catch (error) {
      console.error("Failed to switch organization:", error);
      throw error;
    } finally {
      setIsSwitching(false);
    }
  }, [setActive]);
  
  const isLoading = !authLoaded || !orgLoaded || !listLoaded;
  
  // Helper to check if user is admin (org:admin or owner role)
  const isAdmin = currentOrganization?.role === 'org:admin' || 
                  currentOrganization?.role === 'admin' ||
                  currentOrganization?.role === 'owner';
  
  // Helper to check if user is owner
  const isOwner = currentOrganization?.role === 'owner';
  
  // Helper to check if user is member
  const isMember = currentOrganization?.role === 'org:member' || 
                   currentOrganization?.role === 'member';
  
  return {
    // Current state
    organizations,
    currentOrganization,
    isLoading: isLoading || isSwitching,
    
    // Actions
    switchOrganization,
    
    // Helpers
    userRole: currentOrganization?.role || null,
    isOwner,
    isAdmin,
    isMember,
    
    // Clerk-specific
    clerkOrganization: clerkOrg,
  };
}
